import { ModalTools } from "@Utilities/ModalTools";
import type { ValidationService } from "aspnet-client-validation";

export class UnobtrusiveValidationAjax
{
	private readonly DataClick = "unobtrusiveAjaxClick";
	private readonly DataTarget = "unobtrusiveAjaxClickTarget";
	private readonly Service: ValidationService;

	constructor(validationService: ValidationService)
	{
		this.Service = validationService;
		this.DelegateEvents();
	}

	public DelegateEvents = () =>
	{
		document.addEventListener("click", this.HandleClick);
		document.addEventListener("submit", this.HandleSubmit);
	};

	public HandleClick = (e: Event) =>
	{
		const rawTarget = e.target as HTMLElement;

		const ajaxLink = rawTarget.closest("a[data-ajax=true]");
		const imageSubmit = rawTarget.closest("form[data-ajax=true] input[type=image]");
		const submitButton = rawTarget.closest(
			"form[data-ajax=true] button[type=submit], form[data-ajax=true] input[type=submit]"
		);

		if (ajaxLink)
		{
			e.preventDefault();

			this.AsyncRequest(ajaxLink as HTMLElement, {
				url: (ajaxLink as HTMLAnchorElement).href,
				type: "GET",
				data: []
			});
		}
		else if (imageSubmit)
		{
			if (e instanceof MouseEvent)
			{
				const input = imageSubmit as HTMLInputElement;
				const form = input.closest("form") as HTMLFormElement;
				const rect = input.getBoundingClientRect();

				const clickData = [
					{
						name: input.name + ".x",
						value: Math.round(e.clientX - rect.left)
					},
					{
						name: input.name + ".y",
						value: Math.round(e.clientY - rect.top)
					}
				];

				(form as any)[this.DataClick] = clickData;

				setTimeout(() =>
				{
					delete (form as any)[this.DataClick];
				}, 0);
			}
		}
		else if (submitButton)
		{
			const button = submitButton as HTMLButtonElement | HTMLInputElement;
			const form = button.closest("form") as HTMLFormElement;

			(form as any)[this.DataClick] =
				button.name
					? [{ name: button.name, value: button.value }]
					: [];

			(form as any)[this.DataTarget] = button;

			setTimeout(() =>
			{
				delete (form as any)[this.DataClick];
				delete (form as any)[this.DataTarget];
			}, 0);
		}
	};

	public HandleSubmit = (e: SubmitEvent) =>
	{
		const form = e.target as HTMLFormElement;
		if (form.matches("form[data-ajax=true]"))
		{
			const clickInfo = (form as any)[this.DataClick] || [];
			const clickTarget = (form as any)[this.DataTarget] as HTMLElement | undefined;
			const isCancel = clickTarget && (clickTarget.classList.contains("cancel") || clickTarget.hasAttribute("formnovalidate"));
			e.preventDefault();
			if (!isCancel && !this.Validate(form))
			{
				return;
			}
			const formDataArr = Array.from(new FormData(form))
				.filter(([, value]) => !(value instanceof File))
				.map(([name, value]) => ({ name, value: value as string }));
			this.AsyncRequest(form, {
				url: form.action,
				type: form.method || "GET",
				data: clickInfo.concat(formDataArr)
			});
		}
	};

	public GetFunction = (code: string | null, argNames: string[]): Function =>
	{
		if (!code) return () => {};
		let fn: any = window;
		const parts = code.split(".");
		while (fn && parts.length)
		{
			fn = fn[parts.shift()!];
		}
		if (typeof fn === "function")
		{
			return fn;
		}
		return () => { throw new Error(`Handler not found: ${code}`); };
	};

	public IsMethodProxySafe = (method: string): boolean =>
	{
		return method === "GET" || method === "POST";
	};

	public AjaxOnBegin = (xhr: XMLHttpRequest): boolean =>
	{
		// Default begin handler can be overridden by defining a function specified in data-ajax-begin attribute
		return true;
	}

	public AjaxOnComplete = (xhr: XMLHttpRequest) =>
	{
		return;
	}

	public AjaxOnFailure = (xhr: XMLHttpRequest) =>
	{
		ModalTools.DisplayAlert("An error occurred while processing your request. Please try again.");
	}

	public AjaxOnSuccess = (xhr: XMLHttpRequest) =>
	{
		return;
	}

	public AsyncOnBeforeSend = (xhr: XMLHttpRequest, method: string) =>
	{
		if (!this.IsMethodProxySafe(method))
		{
			xhr.setRequestHeader("X-HTTP-Method-Override", method);
		}
	};

	public AsyncOnSuccess = (element: HTMLElement, data: string, contentType: string = "") =>
	{
		if (contentType.indexOf("application/x-javascript") !== -1) return;
		const mode = (element.getAttribute("data-ajax-mode") || "").toUpperCase();
		const updateSelector = element.getAttribute("data-ajax-update");
		if (!updateSelector) return;
		document.querySelectorAll(updateSelector).forEach(update =>
		{
			switch (mode)
			{
				case "BEFORE":
					update.insertAdjacentHTML("afterbegin", data);
					break;
				case "AFTER":
					update.insertAdjacentHTML("beforeend", data);
					break;
				case "REPLACE-WITH":
					update.outerHTML = data;
					break;
				default:
					(update as HTMLElement).innerHTML = data;
					break;
			}
		});
	};

	public AsyncRequest = (element: HTMLElement, options: any) =>
	{
		const confirmMsg = element.getAttribute("data-ajax-confirm");
		if (confirmMsg && !window.confirm(confirmMsg))
		{
			return;
		}

		const loadingSelector = element.getAttribute("data-ajax-loading");
		const loading = loadingSelector ? document.querySelector(loadingSelector) as HTMLElement : null;
		const loadingDuration = parseInt(element.getAttribute("data-ajax-loading-duration") || "0", 10) || 0;

		options.type = element.getAttribute("data-ajax-method") || options.type;
		options.url = element.getAttribute("data-ajax-url") || options.url;
		options.cache = (element.getAttribute("data-ajax-cache") || "").toLowerCase() === "true";

		if (!options.data) options.data = [];
		options.data.push({ name: "X-Requested-With", value: "XMLHttpRequest" });

		let method = (options.type || "GET").toUpperCase();
		if (!this.IsMethodProxySafe(method))
		{
			options.type = "POST";
			options.data.push({ name: "X-HTTP-Method-Override", value: method });
		}

		// Handle multipart/form-data
		if (element instanceof HTMLFormElement && element.enctype.toLowerCase().startsWith("multipart/form-data")
		)
		{
			const formdata = new FormData();

			options.data.forEach((v: any) =>
			{
				formdata.append(v.name, v.value);
			});

			element.querySelectorAll("input[type=file]").forEach(input =>
			{
				const fileInput = input as HTMLInputElement;

				if (fileInput.files)
				{
					Array.from(fileInput.files).forEach(file =>
					{
						formdata.append(fileInput.name, file);
					});
				}
			});

			options.processData = false;
			options.contentType = false;
			options.data = formdata;
		}

		if (method === "GET" && options.processData !== false)
		{
			const getParams = (options.data as Array<{ name: string; value: string }>)
				.map(v => encodeURIComponent(v.name) + "=" + encodeURIComponent(String(v.value)))
				.join("&");
			if (getParams)
			{
				const sep = options.url.indexOf("?") !== -1 ? "&" : "?";
				options.url = options.url + sep + getParams;
			}
		}

		if (!options.cache && method === "GET")
		{
			const sep = options.url.indexOf("?") !== -1 ? "&" : "?";
			options.url = options.url + sep + "_=" + Date.now();
		}

		const xhr = new XMLHttpRequest();
		xhr.open(options.type, options.url, true);
		if (options.contentType !== false && method !== "GET")
		{
			xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded; charset=UTF-8");
		}
		xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

		xhr.onload = () =>
		{
			if (xhr.status >= 200 && xhr.status < 300)
			{
				this.AsyncOnSuccess(element, xhr.responseText, xhr.getResponseHeader("Content-Type") || "");

				try
				{
					this.GetFunction(element.getAttribute("data-ajax-success"), ["data", "status", "xhr"]).apply(element, [xhr.responseText, xhr.statusText, xhr]);
				}
				catch (e)
				{
					this.AjaxOnSuccess(xhr);
				}
			}
			else
			{
				const failureAttr = element.getAttribute("data-ajax-failure");
				if (failureAttr)
				{
					try
					{
						this.GetFunction(failureAttr, ["xhr", "status", "error"]).apply(element, [xhr, xhr.statusText, new Error("Network error")]);
					}
					catch (e)
					{
						this.AjaxOnFailure(xhr);
					}
				}
				else
				{
					this.AjaxOnFailure(xhr);
				}
			}
			if (loading) this.HideLoading(loading, loadingDuration);

			try
			{
				this.GetFunction(element.getAttribute("data-ajax-complete"), ["xhr", "status"]).apply(element, [xhr, xhr.statusText]);
			}
			catch (e)
			{
				this.AjaxOnComplete(xhr);
			}
		};

		xhr.onerror = () =>
		{
			const failureAttr = element.getAttribute("data-ajax-failure");
			if (failureAttr)
			{
				try
				{
					this.GetFunction(failureAttr, ["xhr", "status", "error"]).apply(element, [xhr, xhr.statusText, new Error("Network error")]);
				}
				catch (e)
				{
					this.AjaxOnFailure(xhr);
				}
			}
			else
			{
				this.AjaxOnFailure(xhr);
			}

			if (loading) this.HideLoading(loading, loadingDuration);

			try
			{
				this.GetFunction(element.getAttribute("data-ajax-complete"), ["xhr", "status"]).apply(element, [xhr, xhr.statusText]);
			}
			catch (e)
			{
				this.AjaxOnComplete(xhr);
			}
		};

		this.AsyncOnBeforeSend(xhr, method);

		let beginResult = true;

		try
		{
			beginResult = this.GetFunction(element.getAttribute("data-ajax-begin"), ["xhr"]).apply(element, [xhr]);
		}
		catch (e)
		{
			beginResult = this.AjaxOnBegin(xhr);
		}

		if (beginResult === false) return;
		if (loading) this.ShowLoading(loading, loadingDuration);

		if (options.processData === false && options.data instanceof FormData)
		{
			xhr.send(options.data);
		}
		else if (method === "GET")
		{
			xhr.send(null);
		}
		else
		{
			const params = (options.data as Array<{ name: string; value: string }>)
				.map(v => encodeURIComponent(v.name) + "=" + encodeURIComponent(String(v.value)))
				.join("&");
			xhr.send(params);
		}
	};

	private ShowLoading = (el: HTMLElement, duration: number) =>
	{
		if (duration > 0)
		{
			el.style.transition = `opacity ${duration}ms`;
			el.style.opacity = "0";
			el.style.display = "";
			requestAnimationFrame(() => { el.style.opacity = "1"; });
		}
		else
		{
			el.style.display = "";
		}
	};

	private HideLoading = (el: HTMLElement, duration: number) =>
	{
		if (duration > 0)
		{
			el.style.transition = `opacity ${duration}ms`;
			el.style.opacity = "0";
			setTimeout(() => { el.style.display = "none"; }, duration);
		}
		else
		{
			el.style.display = "none";
		}
	};

	public Validate = (form: HTMLFormElement): boolean =>
	{
		return this.Service.isValid(form);
	};
}